// ============================================================================
// Hooks TanStack Query — EQUIPES (E1b) : GET /api/teams + fiche + objectifs
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). La logique d'autorisation vit
// au SERVEUR (viewer renvoye par l'API — jamais recalcule au callsite, lecon
// GA-05) ; le SPA consomme viewer.* pour les affordances UI.
// Erreurs mutation -> code court ?error= (deep-linkable, parite F-402/F-410)
// mappe sur un message i18n par les pages (lecon GA-21).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiForbiddenError, ApiServerError } from '@gs/api-client';

export type ObjectiveStatus = 'pending' | 'in_progress' | 'done';
export type GestTeamRole = 'admin' | 'manager' | 'director' | 'employee';

// ─── Liste (F-206..F-212) ────────────────────────────────────────────────────

/**
 * Entree du calcul des distinctions (F-193). ANTI-FUITE M-208 : `narrativeLength`
 * est la LONGUEUR du narratif, jamais son texte — /equipes est ouvert a tout le
 * management (manager inclus) alors que le narratif est reserve a admin OU au
 * directeur de l'equipe. computeTeamBadges consomme la longueur telle quelle
 * (BadgePulse.narrative_length), donc le palmares est identique pour tous.
 */
export interface TeamBadgePulse {
  period: string;
  moodScore: number;
  narrativeLength: number;
}

export interface TeamSummary {
  id: string;
  name: string;
  department: string | null;
  headcount: number;
  directorEmail: string | null;
  directorName: string | null;
  memberCount: number;
  latestMood: number | null;
  latestPeriod: string | null;
  currentMood: number | null;
  submittedThisPeriod: boolean;
  objectivesTotal: number;
  objectivesDone: number;
  objectivesInProgress: number;
  /** Pulses de la fenetre badges (6 mois) — longueurs seulement (M-208). */
  badgePulses: TeamBadgePulse[];
}

export interface TeamsListKpis {
  period: string;
  teamCount: number;
  totalHeadcount: number;
  moodAvg: number | null;
  submittedCount: number;
  objectivesTotal: number;
  objectivesDone: number;
  objectivesInProgress: number;
}

export interface TeamsListResponse {
  teams: TeamSummary[];
  kpis: TeamsListKpis;
  /** Periode courante des distinctions (F-193) — fenetre de 6 mois calee dessus. */
  badgePeriod: string;
  viewer: { role: GestTeamRole };
}

// ─── Fiche (F-216..F-224) ─────────────────────────────────────────────────────

export interface TeamPulse {
  id: string;
  period: string;
  moodScore: number;
  /** Detail narratif (PII M-208) — null si le viewer n'y a pas droit. */
  challenges: string | null;
  positives: string | null;
  supportNeeded: string | null;
}

export interface TeamObjective {
  id: string;
  title: string;
  status: ObjectiveStatus;
  targetDate: string | null;
}

export interface TeamMember {
  email: string;
  fullName: string | null;
  jobTitle: string | null;
  isDirector: boolean;
}

export interface TeamPosting {
  id: string;
  title: string;
  description: string | null;
  deadline: string | null;
}

export interface TeamDetailResponse {
  team: {
    id: string;
    name: string;
    department: string | null;
    headcount: number;
    directorEmail: string | null;
    directorName: string | null;
    createdAt: string;
  };
  year: number;
  pulses: TeamPulse[];
  objectives: TeamObjective[];
  members: TeamMember[];
  postings: TeamPosting[];
  viewer: {
    role: GestTeamRole;
    email: string;
    canWriteObjectives: boolean;
    canSeePulseDetail: boolean;
  };
}

export interface ObjectiveWritePayload {
  title: string;
  status: ObjectiveStatus;
  targetDate: string | null;
}

const LIST_KEY = ['gest', 'teams'] as const;
const detailKey = (id: string) => ['gest', 'teams', id] as const;

/** Code court d'erreur pour `?error=` (parite deep-link) — jamais de code brut (GA-21). */
export function teamErrorCode(err: unknown): string {
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
  }
  return 'server';
}

export function useTeams(enabled: boolean) {
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: () => api.get<TeamsListResponse>('/api/teams'),
    enabled,
  });
}

export function useTeamDetail(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: detailKey(id ?? ''),
    queryFn: () => api.get<TeamDetailResponse>(`/api/teams/${id}`),
    enabled: enabled && !!id,
    // 404 (id inconnu, F-217) et 403 = etats stables — pas de retry.
    retry: false,
  });
}

function useInvalidateTeam(id: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: LIST_KEY });
    void qc.invalidateQueries({ queryKey: detailKey(id) });
  };
}

/** F-225 : creation d'un objectif (admin OU director de l'equipe, M-204). */
export function useCreateObjective(teamId: string) {
  const invalidate = useInvalidateTeam(teamId);
  return useMutation({
    mutationFn: (payload: ObjectiveWritePayload) =>
      api.post<{ id: string }>(`/api/teams/${teamId}/objectives`, payload),
    onSuccess: () => invalidate(),
  });
}

/** F-226/F-227 : maj title/status/target_date (M-204/M-206). */
export function useUpdateObjective(teamId: string) {
  const invalidate = useInvalidateTeam(teamId);
  return useMutation({
    mutationFn: (input: { id: string; payload: ObjectiveWritePayload }) =>
      api.put<{ ok: boolean }>(`/api/teams/${teamId}/objectives/${input.id}`, input.payload),
    onSuccess: () => invalidate(),
  });
}

/** F-228 : soft-delete + audit objective.deleted (M-207). */
export function useDeleteObjective(teamId: string) {
  const invalidate = useInvalidateTeam(teamId);
  return useMutation({
    mutationFn: (objectiveId: string) =>
      api.delete<{ ok: boolean }>(`/api/teams/${teamId}/objectives/${objectiveId}`),
    onSuccess: () => invalidate(),
  });
}
